/**
 * Centralised, typed access to environment configuration.
 *
 * All runtime configuration flows through this module so that the rest of the
 * application never reads `import.meta.env` directly. This keeps configuration
 * discoverable, typed, and easy to mock in tests.
 */

function readString(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Authentication mode:
 *  - `bff`  → real OAuth2-BFF session auth (cookie session managed by the BFF;
 *             session/permissions/tenants are bootstrapped from `GET /api/v1/me`).
 *  - `mock` → in-browser mock session (no backend) for local UI work and tests.
 *
 * Defaults to `mock` so the app and the test suite run offline; production
 * builds set `VITE_AUTH_MODE=bff`.
 */
export type AuthMode = 'bff' | 'mock';

function readAuthMode(value: string | undefined): AuthMode {
  return value?.trim().toLowerCase() === 'bff' ? 'bff' : 'mock';
}

export interface AppEnv {
  /** Base URL of the API gateway / BFF. */
  readonly apiBaseUrl: string;
  /** Human-readable application name. */
  readonly appName: string;
  /** Default tenant identifier used until a real tenant is resolved. */
  readonly defaultTenantId: string;
  /** Active authentication mode. */
  readonly authMode: AuthMode;
  /** Active Vite mode (e.g. "development", "production", "test"). */
  readonly mode: string;
  /** True when running a production build. */
  readonly isProduction: boolean;
  /** True when running in development. */
  readonly isDevelopment: boolean;
}

export const env: AppEnv = {
  apiBaseUrl: readString(import.meta.env.VITE_API_BASE_URL, 'http://localhost:8080'),
  appName: readString(import.meta.env.VITE_APP_NAME, 'Merchant Portal'),
  defaultTenantId: readString(import.meta.env.VITE_DEFAULT_TENANT_ID, 'demo-tenant'),
  authMode: readAuthMode(import.meta.env.VITE_AUTH_MODE),
  mode: import.meta.env.MODE,
  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
};
